<template>
  <tab-item-template>
    <template #toolbar-items>
      <v-spacer></v-spacer>
      <v-text-field
          v-model="filterText"
          clearable
          outlined
          dense
          placeholder="検索"
          light
          background-color="white"
          style="max-width: 300px;"
          hide-details>
      </v-text-field>
    </template>

    <el-table
        :data="serviceProfileViewFiltered"
        border>
      <el-table-column
          label="名称"
          prop="name"
          width="300"
          sortable>
      </el-table-column>
      <el-table-column
          label="説明"
          prop="description"
          sortable>
        <template #default="{row}">
          <table-inline>
            <text-field-like
                :value="row.description"
                placeholder="未設定"
                multi-line>
            </text-field-like>
          </table-inline>
        </template>
      </el-table-column>
      <el-table-column
          width="200"
          label="共通辞書"
          prop="dictionaryName"
          sortable>
        <template #default="{row}">
          <table-inline>
            <text-field-like
                :value="row.dictionaryName"
                :value-hds-id="row.dictionaryId">
            </text-field-like>
          </table-inline>
        </template>
      </el-table-column>
      <el-table-column
          width="200"
          label="コントローラ"
          prop="controller"
          sortable>
      </el-table-column>
      <el-table-column
          label="公開"
          prop="publishing"
          align="center"
          width="70">
        <template #default="{row}">
          <table-inline>
            <el-checkbox
                :value="row.serviceImplementation && row.serviceImplementation.publishing"
                @change="handlePublishingChange(row, $event)">
            </el-checkbox>
          </table-inline>
        </template>
      </el-table-column>
      <el-table-column
          width="200"
          align="center"
          fixed="right"
          column-key="operations"
          class-name="operations">
        <template #default="{row}">
          <v-btn
              v-if="row.produceId"
              depressed
              color="blue-grey white--text"
              :to="{name: '/profileManagement/serviceProfile', params: {serviceProfileId: row.produceId}}">
            <v-icon>el-icon-view</v-icon>
            提供
          </v-btn>
          <v-btn
              v-if="row.consumeId"
              depressed
              color="blue-grey darken-1 white--text"
              :to="{name: '/profileManagement/serviceProfile', params: {serviceProfileId: row.consumeId}}">
            <v-icon>el-icon-view</v-icon>
            利用
          </v-btn>
        </template>
      </el-table-column>
    </el-table>
  </tab-item-template>
</template>

<script>
import TabItemTemplate from "@/components/templates/TabItemTemplate.vue";
import {mapGetters, mapState} from "vuex";
import TableInline from "@/components/atoms/TableInline.vue";
import {SERVICE_TYPES} from "@/constants";
import TextFieldLike from "@/components/molecules/TextFieldLike.vue";
import Swal from "sweetalert2";
import groupArray from "group-array";

export default {
  name: "ServiceProfileTab",
  components: {TextFieldLike, TableInline, TabItemTemplate},
  props: {
    controllers: {
      type: Array,
      required: true,
    },
    serviceProfiles: {
      type: Array,
      required: true,
    },
    serviceComponentModels: {
      type: Array,
      required: true,
    }
  },
  data() {
    return {
      serviceTypes: SERVICE_TYPES,
      filterText: '',
    };
  },
  computed: {
    ...mapGetters({
      hcsClient: 'hcs/client',
    }),
    serviceProfileView() {
      return Object.values(groupArray(this.serviceProfiles, sp => `${sp.serviceImplementationId} ${sp.dictionaryId} ${sp.serviceComponentModelId}`)).map(group => {
        const first = group[0];
        if(first === undefined) {
          return undefined;
        }
        const si = first.serviceImplementation;
        const controller = this.controllers.find(c => c.id === si?.controllerId);
        const commonScm = this.serviceComponentModels.find(s => s.id === first.serviceComponentModelId);
        const localScm = this.serviceComponentModels.find(s => s.id === si?.serviceComponentModelId);
        const consumeServiceProfile = group.find(sp => sp.serviceType === 'consume');
        const produceServiceProfile = group.find(sp => sp.serviceType === 'produce');
        return {
          ...first,
          name: localScm?.name,
          description: localScm?.description,
          dictionaryId: commonScm?.dictionary?.id,
          dictionaryName: commonScm?.dictionary?.name,
          serviceImplementation: si,
          controller: controller?.name,
          consumeId: consumeServiceProfile?.readProcessOperationImplementationsCount > 0 ? consumeServiceProfile.id : null,
          produceId: produceServiceProfile?.createProcessOperationImplementationsCount > 0 ? produceServiceProfile.id : null,
          serviceType: undefined,
          id: undefined,
        }
      }).filter(dpv => dpv.consumeId || dpv.produceId);
    },
    serviceProfileViewFiltered() {
      const f = this.filterText;
      if(f) {
        return this.serviceProfileView.filter(dp =>
            dp.name.includes(f) ||
            dp.description?.includes(f) ||
            dp.dictionaryName.includes(f) ||
            dp.controller.includes(f)
        );
      } else {
        return this.serviceProfileView;
      }
    },
  },
  methods: {
    async handlePublishingChange(serviceProfile) {
      const isPublishing = !serviceProfile.serviceImplementation.publishing;
      const {title, html} = (() => {
        if(isPublishing) {
          return {
            title: 'サービスプロファイルの公開',
            html: `サービスプロファイルに関連するサービス実装「${serviceProfile.name}」を公開します。よろしいですか？`
          };
        } else {
          return {
            title: 'サービスプロファイルの非公開',
            html: `サービスプロファイルに関連するサービス実装「${serviceProfile.name}」を非公開にします。よろしいですか？`
          };
        }
      })();

      const {value: confirm} = await Swal.fire({
        title,
        html,
        icon: 'info',
        showCloseButton: true,
        showCancelButton: true,
        focusConfirm: true,
        confirmButtonText: "はい",
        cancelButtonText: "いいえ"
      });
      if(!confirm) {
        return;
      }

      serviceProfile.serviceImplementation.publishing = isPublishing;

      await this.hcsClient.patch(`/service_implementations/${serviceProfile.serviceImplementation.id}`, {
        publishing: isPublishing
      });
    }
  },
}
</script>

<style lang="scss" scoped src="./Operations.scss">
</style>
